// eslint-disable-next-line import/no-unresolved
import { SharingLink } from '@microsoft/microsoft-graph-types';
import moment from 'moment-timezone';

import { AxiosError } from 'axios';
import { MNSproCloudApi } from '../../mnspro-cloud';
import {
  CopyType,
  CourseDriveItemResponse,
  DocumentsGetFilesResponse,
  DocumentsZipSelection,
  DocumentsZipStatusResponse,
  DriveItem,
  ExchangeAuthor,
  FileDownload,
  IdentitySet,
} from '../../mnspro-cloud/api/v2';
import { createGraphApi } from '../factory';
import { ValueOf } from '../helper/helper';
export const COURSE_MATERIAL_DRIVE_NAME = 'Kursmaterialien';
export const COURSE_MATERIAL_LEGACY_FOLDER_NAME = 'Material';
export const COURSE_MATERIAL_FOLDER_PREFIX = 'coursematerial::';
export interface FileItem extends DriveItem {
  type?: DriveType;
}

/**
 * Converts the backend `ExchangeAuthor` DTO into the Graph-shaped `IdentitySet`
 * used by `DriveItem`/`FileItem`, so created folders carry the same
 * "modified by" / "created by" info the list already renders.
 */
const toIdentitySet = (author?: ExchangeAuthor): IdentitySet | undefined =>
  author?.user
    ? {
        user: {
          id: author.user.id ?? null,
          displayName: author.user.displayName ?? null,
        },
      }
    : undefined;

interface CreateUploadSessionResponse {
  expirationDateTime: string;
  nextExpectedRanges: string[];
  uploadUrl: string;
}

export interface SerializedFile {
  name: string;
  size: number;
  contentType: string;
  objectUrl: string;
}

export interface Drive {
  label: string | undefined;
  // Group or drive id, depending on type
  id: string | undefined;
  type: DriveType;
  driveId: null | string;
}

export enum DriveType {
  Personal = 'personal',
  Group = 'group',
  SchoolWide = 'schoolwide',
}

interface CreateShareLinkResponse {
  id: string;
  roles: string[];
  link: SharingLink;
  hasPassword: boolean;
}

/**
 * Defines the write permissions when creating a new
 * Folder.
 */
export enum FolderWritePermissions {
  none = 'None',
  ownersOnly = 'OwnersOnly',
  allUsers = 'AllUsers',
}

/**
 * Describes the type the new file a user is able to
 * create directly browsing the drives/folders
 */
export enum NewFileType {
  Folder = 'folder',
  Docx = 'docx',
  Xlsx = 'xlsx',
  Pptx = 'pptx',
}

/**
 * Defines who can access a shared file
 */
export enum ShareScope {
  Organization = 'organization',
  Anonymous = 'anonymous',
}

/**
 * Defines the access level of shared file
 */
export enum ShareType {
  View = 'view',
  Edit = 'edit',
}

/**
 * Indicates success or the type of error for deleting files
 */
export enum FileOperationResult {
  Success = 'success',
  ErrorLocked = 'errorlocked',
  ErrorNameConflict = 'errorNameConflict',
  ErrorUnknown = 'errorUnknown',
}

/**
 * Chunk size for every file upload (4 MB)
 */
const UPLOAD_CHUNK_SIZE_BYTES = 4e6;

/**
 * Creates an upload session at the given Graph URL and streams the file's
 * bytes in chunks. Returns <code>false</code> if any chunk PUT fails.
 */
async function uploadFileViaSession(
  authToken: string,
  createUploadSessionUrl: string,
  file: SerializedFile,
): Promise<boolean> {
  const createUploadSessionResponse = await createGraphApi(
    authToken,
  ).post<CreateUploadSessionResponse>(createUploadSessionUrl, {
    '@microsoft.graph.conflictBehavior': 'rename',
  });

  const response = await fetch(file.objectUrl);
  const blob = await response.blob();

  const fragmentsTotalCount = Math.ceil(blob.size / UPLOAD_CHUNK_SIZE_BYTES);
  let fragmentsIndex = 0;
  let bytesRemaining = blob.size;

  while (fragmentsIndex < fragmentsTotalCount) {
    const step = (fragmentsIndex + 1) * UPLOAD_CHUNK_SIZE_BYTES;
    const from = fragmentsIndex * UPLOAD_CHUNK_SIZE_BYTES;
    const to = bytesRemaining > UPLOAD_CHUNK_SIZE_BYTES ? step - 1 : blob.size - 1;
    bytesRemaining = Math.max(blob.size - step, 0);

    try {
      await createGraphApi(authToken).put(
        createUploadSessionResponse.data.uploadUrl,
        blob.slice(from, to + 1),
        {
          headers: {
            'Content-Type': 'application/octet-stream',
            'Content-Range': `bytes ${from}-${to}/${blob.size}`,
          },
        },
      );
    } catch {
      return false;
    }

    ++fragmentsIndex;
  }

  return true;
}

export const DriveService = {
  /**
   * Enqueues the provisioning process for the users one drive.
   * This process can take up to 24 hours.
   * Will return if provisioning is done or already enqueued.
   * Will throw ErrorResponse if provisioning is still in process.
   * @param authToken
   * @param organizationId
   */
  async provisionPersonalDrive(authToken: string, organizationId: string): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const documents = MNSproCloudApi.V2.DocumentsApiFactory(config);

    await documents.provision(organizationId);
  },

  /**
   * Retrieves the record for the personal one drive
   */
  async getPersonalRootDrive(authToken: string): Promise<DriveItem> {
    const response = await createGraphApi(authToken).get<DriveItem>('/me/drive/root');

    return response.data;
  },

  async getFilesOfUser(
    authToken: string,
    organizationId: string,
    folderId?: string,
    timePeriod?: string,
  ): Promise<DocumentsGetFilesResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.DocumentsApiFactory(config);

    const response = await api.getFilesOfUser(organizationId, timePeriod, folderId);

    return response.data;
  },

  async getDownloadUrlOf(
    authToken: string,
    organizationId: string,
    fileId: string,
    timePeriod?: string,
  ): Promise<FileDownload> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.DocumentsApiFactory(config);

    const response = await api.getDownloadUrlOf(organizationId, fileId, timePeriod);

    return response.data;
  },

  /**
   * Starts building a ZIP archive of the selected files and folders in the background.
   * Resolves to the job id; an identical selection that is still being built resolves
   * to the id of the running job instead of starting a second one. Throws when no id is returned.
   * Track progress via `TasksService.getJobState`, then fetch the URL via `getZipStatus`.
   * @param authToken
   * @param organizationId
   * @param selection Graph item ids plus their drive id (Personal/Group),
   * or ParentFile ids (SchoolWide)
   */
  async startZip(
    authToken: string,
    organizationId: string,
    selection: DocumentsZipSelection,
  ): Promise<string> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.DocumentsApiFactory(config);

    const response = await api.zip(organizationId, selection);

    if (!response.data.id) {
      throw new Error('The ZIP request returned no job id');
    }

    return response.data.id;
  },

  /**
   * Returns the download URL of a finished ZIP archive job, or its current
   * message and severity while no URL is available yet.
   * @param authToken
   * @param organizationId
   * @param jobId Id returned by `startZip`
   */
  async getZipStatus(
    authToken: string,
    organizationId: string,
    jobId: string,
  ): Promise<DocumentsZipStatusResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.DocumentsApiFactory(config);

    const response = await api.zipStatus(organizationId, jobId);

    return response.data;
  },

  /**
   * Retrieves the contents of the root folder of the users' personal
   * one drive.
   */
  async getRootFolder(authToken: string): Promise<DriveItem[]> {
    const response = await createGraphApi(authToken).get<ValueOf<DriveItem[]>>(
      '/me/drive/root/children',
    );

    return response.data.value;
  },

  /**
   * Retrieves the contents/files of the given subfolder
   */
  async getFolder(authToken: string, drive: Drive, folderId: string): Promise<DriveItem[]> {
    const url =
      drive.type === DriveType.Group
        ? `/groups/${drive.id}/drive/items/${folderId}/children`
        : `/me/drive/items/${folderId}/children`;

    const response = await createGraphApi(authToken).get<ValueOf<DriveItem[]>>(url);

    return response.data.value;
  },

  /**
   * Retrieves the root of group
   */
  async getGroupRoot(authToken: string, groupId: string): Promise<DriveItem> {
    const url = `/groups/${groupId}/drive/root`;

    const response = await createGraphApi(authToken).get<DriveItem>(url);

    return response.data;
  },

  /**
   * Retrieves the contents of a folder from a specific drive by driveId.
   * Used for accessing non-default drives like the Kursmaterialien drive.
   */
  async getDriveFolder(authToken: string, driveId: string, folderId: string): Promise<DriveItem[]> {
    const url =
      folderId === 'root'
        ? `/drives/${driveId}/root/children`
        : `/drives/${driveId}/items/${folderId}/children`;

    const response = await createGraphApi(authToken).get<ValueOf<DriveItem[]>>(url);

    return response.data.value;
  },

  /**
   * Retrieves the Kursmaterialien drive for a group, if it exists.
   */
  async getGroupMaterialDrive(authtoken: string, groupId: string): Promise<DriveItem | undefined> {
    const url = `/groups/${groupId}/drives`;

    const response = await createGraphApi(authtoken).get<ValueOf<DriveItem[]>>(url);

    const drive = response.data.value.find((d) => d.name === COURSE_MATERIAL_DRIVE_NAME);
    return drive;
  },

  /**
   * Move Files from one folder to another
   */
  async moveFiles(
    graphToken: string,
    authToken: string,
    organizationId: string,
    items: FileItem[],
    destinationFolderId?: string,
    destinationDriveId?: string,
    destinationGroupId?: string,
    copy?: boolean,
  ): Promise<boolean> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.DocumentsApiFactory(config);

    let targetDrive = destinationDriveId;
    let groupRootFolderTarget = destinationFolderId;
    if (destinationGroupId) {
      const x = await DriveService.getGroupRoot(graphToken, destinationGroupId);
      groupRootFolderTarget = x.id ?? undefined;
      targetDrive = x.parentReference?.driveId ?? undefined;
    } else {
      targetDrive = destinationDriveId ?? items[0].parentReference?.driveId ?? undefined;
    }

    const targetFolder =
      destinationFolderId === destinationDriveId ? groupRootFolderTarget : destinationFolderId;

    const result = await api.copy(
      organizationId,
      items.map((a) => a.id) as string[],
      items[0].parentReference?.driveId ?? undefined,
      targetDrive,
      targetFolder,
      copy ? CopyType.copy : CopyType.move,
    );

    return result.status === 200;
  },

  /*
  /**
   * Uploads a list of provided files to the drive identified by <code>driveId</code>
   * and the subfolder described by the path in <code>folderPath</code>
   */
  async upload(
    authToken: string,
    drive: Drive,
    folderPath: string,
    fileList: SerializedFile[],
  ): Promise<boolean> {
    for (const file of fileList) {
      const url =
        drive.type === DriveType.Personal
          ? `/me/drive/items/${drive.driveId}:${folderPath}/${file.name}:/createUploadSession`
          : `/groups/${drive.id}/drive/items/${drive.driveId}:${folderPath}/${file.name}:/createUploadSession`;

      const uploaded = await uploadFileViaSession(authToken, url, file);

      if (!uploaded) {
        return false;
      }
    }

    return true;
  },

  /**
   * Uploads files into a specific folder on a specific drive, addressed via
   * <code>/drives/{driveId}/...</code>. Required for drives that aren't the
   * group's default drive (e.g. the Kursmaterialien document library).
   *
   * @param folderId The target folder's item id, or <code>'root'</code> to
   * upload into the drive's root.
   */
  async uploadToDrive(
    authToken: string,
    driveId: string,
    folderId: string,
    fileList: SerializedFile[],
  ): Promise<boolean> {
    for (const file of fileList) {
      const url =
        folderId === 'root'
          ? `/drives/${driveId}/root:/${file.name}:/createUploadSession`
          : `/drives/${driveId}/items/${folderId}:/${file.name}:/createUploadSession`;

      const uploaded = await uploadFileViaSession(authToken, url, file);

      if (!uploaded) {
        return false;
      }
    }

    return true;
  },

  /**
   * Deletes a list of files stored in the given drive
   */
  async deleteFile(authToken: string, drive: Drive, fileId: string): Promise<FileOperationResult> {
    const url =
      drive.type === DriveType.Personal
        ? `/me/drive/items/${fileId}`
        : `/groups/${drive.id}/drive/items/${fileId}`;

    try {
      await createGraphApi(authToken).delete(url);

      return FileOperationResult.Success;
    } catch (error) {
      return error instanceof AxiosError && error.response?.status === 423
        ? FileOperationResult.ErrorLocked
        : FileOperationResult.ErrorUnknown;
    }
  },

  /**
   * Retrieves the drive id for the given group
   */
  async getDriveForGroup(
    authToken: string,
    groupId: string,
    organizationId: string,
  ): Promise<CourseDriveItemResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.CoursesApiFactory(config);
    const result = await api.getMyDrive(groupId, organizationId);
    return result.data;
  },

  /**
   * Creates a new folder for a group drive and returns the created folder so
   * callers can append it locally instead of re-fetching the whole folder list.
   */
  async createGroupDriveFolder(
    mnsProToken: string,
    organizationId: string,
    groupId: string,
    name: string,
    parentFolderId: string,
    permissions: FolderWritePermissions,
  ): Promise<FileItem> {
    const config = MNSproCloudApi.V2.createConfiguration(mnsProToken);
    const api = MNSproCloudApi.V2.DocumentsApiFactory(config);
    const { data } = await api.createFolder(
      organizationId,
      groupId,
      name,
      permissions,
      parentFolderId,
    );
    return {
      id: data.id ?? undefined,
      name: data.name ?? undefined,
      folder: data.folder ? { childCount: data.folder.childCount ?? 0 } : undefined,
      webUrl: data.webUrl ?? undefined,
      size: data.size ?? 0,
      createdDateTime: data.createdDateTime ?? undefined,
      lastModifiedDateTime: data.lastModifiedDateTime ?? undefined,
      createdBy: toIdentitySet(data.createdBy),
      lastModifiedBy: toIdentitySet(data.lastModifiedBy),
    };
  },

  /**
   * Creates a new folder in the personal one drive and returns the created
   * folder so callers can append it locally instead of re-fetching.
   */
  async createPersonalDriveFolder(
    authToken: string,
    parentFolderId: string,
    name: string,
  ): Promise<FileItem> {
    const response = await createGraphApi(authToken).post<DriveItem>(
      `/me/drive/items/${parentFolderId}/children`,
      {
        '@microsoft.graph.conflictBehavior': 'rename',
        name,
        folder: {},
      },
    );
    return response.data;
  },

  /**
   * Creates a new file directly in the supplied path in the
   * given drive. Returns the file record of the newly created
   * file.
   */
  async createNewFile(
    authToken: string,
    drive: Drive,
    path: string,
    mimeType: string,
    parentId: string,
  ): Promise<DriveItem> {
    let url = '';

    switch (drive.type) {
      case DriveType.Personal:
        url = `/me/drive/root:/${path}:/content`;
        break;
      case DriveType.Group:
        url = `/groups/${drive.id}/drive/items/${parentId}:/${path}:/content`;
        break;
      default:
        throw new Error('Unsupported drive type');
    }

    const response = await createGraphApi(authToken).put<DriveItem>(url, '', {
      headers: {
        'Content-Type': mimeType,
        Accept: 'application/json;odata.metadata=full',
      },
    });

    return response.data;
  },

  /**
   * Converts the given file, identified by fileId, to PDF and
   * returns the PDF contents as string.
   */
  async downloadFileAsPdf(authToken: string, drive: Drive, fileId: string): Promise<ArrayBuffer> {
    const url =
      drive.type === DriveType.Personal
        ? `/me/drive/items/${fileId}/content?format=pdf`
        : `/groups/${drive.id}/drive/items/${fileId}/content?format=pdf`;

    const response = await createGraphApi(authToken).get<ArrayBuffer>(url, {
      responseType: 'arraybuffer',
    });

    return response.data;
  },

  /**
   * Creates a link that can be shared with other users to access
   * the given file identified by fileId that is contained in the
   * given drive.
   */
  async createShareLink(
    authToken: string,
    drive: Drive,
    fileId: string,
    type: string,
    scope: string,
    expirationDate: string | null,
    password: string | null,
  ): Promise<string> {
    const url =
      drive.type === DriveType.Personal
        ? `/me/drive/items/${fileId}/createLink`
        : `/groups/${drive.id}/drive/items/${fileId}/createLink`;

    const response = await createGraphApi(authToken).post<CreateShareLinkResponse>(url, {
      expirationDateTime: expirationDate === null ? undefined : moment(expirationDate).format(),
      scope: scope as string,
      type: type as string,
      password: password === null ? undefined : password,
    });

    const webUrl = response.data.link.webUrl;

    if (webUrl === null || webUrl === undefined) {
      throw new Error('Unable to generate link');
    }

    return webUrl;
  },

  async renameFile(
    authToken: string,
    drive: Drive,
    fileId: string,
    newFilename: string,
  ): Promise<FileOperationResult> {
    const url =
      drive.type === DriveType.Personal
        ? `/me/drive/items/${fileId}`
        : `/groups/${drive.id}/drive/items/${fileId}`;

    try {
      await createGraphApi(authToken).patch<void>(url, {
        name: newFilename,
      });

      return FileOperationResult.Success;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 423) {
          return FileOperationResult.ErrorLocked;
        }
        if (error.response?.data?.error?.code === 'nameAlreadyExists') {
          return FileOperationResult.ErrorNameConflict;
        }
      }

      return FileOperationResult.ErrorUnknown;
    }
  },
};
